import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Per-device theme preference. Three states:
 *   - 'light'  : force light
 *   - 'dark'   : force dark
 *   - 'system' : follow the OS preference live
 *
 * Persists to localStorage under FT_THEME_KEY. The resolved value
 * ('light' | 'dark') is also written to <html data-theme="..."> so the
 * CSS [data-theme="dark"] block kicks in. MUI gets the same value via
 * createMUITheme(resolvedMode) wired up in App.js.
 *
 * Public / pre-login routes (/, /auth, /auth/forgot-password,
 * /auth/reset-password) are pinned to 'light' regardless of preference —
 * the branded landing shouldn't surprise first-time visitors with dark.
 * Keep the path list in sync with the pre-mount script in
 * public/index.html so the first paint and the React-controlled state
 * agree.
 *
 * Pre-mount sync is handled by an inline script in public/index.html so
 * the first paint already matches the preference (no light-flash).
 */

function isPublicRoute(pathname) {
  if (pathname === '/') return true;
  if (pathname === '/auth/change-password') return false; // authenticated route
  if (pathname === '/auth' || pathname.startsWith('/auth/')) return true;
  return false;
}

const FT_THEME_KEY = 'ft-theme-mode';
const VALID_MODES = new Set(['light', 'dark', 'system']);

function readStoredMode() {
  try {
    const raw = localStorage.getItem(FT_THEME_KEY);
    return VALID_MODES.has(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

function getSystemPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveMode(mode, systemPrefersDark) {
  if (mode === 'dark') return 'dark';
  if (mode === 'light') return 'light';
  return systemPrefersDark ? 'dark' : 'light';
}

const ThemeContext = createContext({
  mode: 'system',
  resolvedMode: 'light',
  setMode: () => {},
});

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readStoredMode);
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);
  const location = useLocation();
  const publicRoute = isPublicRoute(location.pathname);

  // Live-track OS preference so a user on 'system' sees the app flip
  // when the OS auto-switches at sunset etc.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemPrefersDark(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  const resolvedMode = useMemo(
    () => (publicRoute ? 'light' : resolveMode(mode, systemPrefersDark)),
    [mode, systemPrefersDark, publicRoute]
  );

  // Reflect to <html data-theme="..."> so the CSS theme block applies.
  // The pre-mount script in index.html already set this once; we keep it
  // in sync on every change after.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.theme = resolvedMode;
    // Also update the <meta name="theme-color"> so the browser chrome
    // (mobile address bar, PWA status bar) matches the surface color.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', resolvedMode === 'dark' ? '#0B1220' : '#ffffff');
  }, [resolvedMode]);

  const setMode = useCallback((next) => {
    if (!VALID_MODES.has(next)) return;
    setModeState(next);
    try { localStorage.setItem(FT_THEME_KEY, next); } catch { /* ignore */ }
  }, []);

  const value = useMemo(
    () => ({ mode, resolvedMode, setMode }),
    [mode, resolvedMode, setMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeMode() {
  return useContext(ThemeContext);
}
